import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  inject
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  Category,
  Filter,
  FilterOption,
  Product,
  ProductGender,
  ProductLabel
} from '@infrab4a/connect';
import { MessageService } from 'primeng/api';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteUnselectEvent
} from 'primeng/autocomplete';
import { ChipsAddEvent } from 'primeng/chips';
import { BaseConnectService } from 'src/app/connect-api/api/shop/base-connect.service';
import { ShopCategoryFilterService } from 'src/app/connect-api/api/shop/shop-category-filter.service';
import { ShopCategoryService } from 'src/app/connect-api/api/shop/shop-category.service';
import { ShopProductGroupService } from 'src/app/connect-api/api/shop/shop-product-group.service';
import { ShopProductService } from 'src/app/connect-api/api/shop/shop-product.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { FormUtil } from 'src/app/utils/form.util';
import { slugify } from 'src/app/utils/slug.util';
import { DropdownFilter } from '../table';

@Component({
  selector: 'app-shop-product-form',
  templateUrl: './shop-product-form.component.html',
  styleUrl: './shop-product-form.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ShopProductFormComponent implements OnInit {
  @Input() product: Partial<Product>;
  @Input() categories: Array<Category>;
  @Input() permission = true;

  @Output() productUpdated = new EventEmitter();

  private productService = inject(ShopProductService);
  private productGroupService = inject(ShopProductGroupService);
  private categoryService = inject(ShopCategoryService);
  private messageService = inject(MessageService);
  private filterService = inject(ShopCategoryFilterService);

  form = this.createFormGroup();
  genders = [
    { name: 'Masculino', value: 'male' },
    { name: 'Feminino', value: 'female' },
    { name: 'Unissex', value: 'unisex' }
  ];
  labels = [
    { name: 'Nenhum', value: 'null' },
    { name: 'Outlet', value: 'outlet' },
    { name: 'Promoção', value: 'on-sale' },
    { name: 'Últimas unidades', value: 'last-units' },
    { name: 'Glamstar', value: 'glamstar' }
  ];
  groups: { name: string; value: number }[] = [];
  validities = [
    { name: 'Sim', value: true },
    { name: 'Não', value: false }
  ];
  categoriesForDropdown: Array<DropdownFilter>;
  chipRegex: any = /\r|\n|;|,/;
  filterOptions: Filter[] = [];
  filterOptionsAvailable: FilterOption[] = [];
  groupedCategories: Array<{
    label: string;
    value: string;
    items: Array<{ label: string; value: string }>;
  }> = [];
  brands: Category[];
  showVariantsOptions = [
    { name: 'Exibir variantes', value: true },
    { name: 'Exibir produto principal', value: false }
  ];

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    this.groupedCategories = this.getCategoryTreeGrouped();

    await Promise.all([
      this.getProductGroups(),
      this.findFiltersOptions(),
      this.getBrandsOptions()
    ]);
    this.categoriesForDropdown = [
      { label: 'Sem categoria', value: BaseConnectService.NULL }
    ].concat(
      this.categories.map((c) => ({
        label: `${c.reference} ${c.name} [${c.shop}]`,
        value: c
      }))
    );
    this.form = this.createFormGroup();

    if (this.product?.category?.id) {
      await this.findCategoryTree(this.product?.category?.id);
    }
    this.recalculatePrices();

    LoaderService.showLoader(false);
  }

  private getCategoryTreeGrouped() {
    const options = [];
    const levelThree = this.categories.filter(
      (category) => category.reference.length >= 5
    );
    const grouped = levelThree.reduce<Record<string, Category[]>>(
      (acc, item) => {
        const prefix = item.reference.slice(0, 3);
        const levelTwo = this.categories.find(
          (category) => category.reference === prefix
        );

        const name = `${prefix} - ${levelTwo?.name?.toString()}`;
        if (!acc[name]) {
          acc[name] = [];
        }

        acc[name].push(item);

        return acc;
      },
      {}
    );

    for (const key in grouped) {
      options.push({
        label: key,
        value: key,
        items: grouped[key]
          .map((option) => {
            return {
              label: `${option.reference} ${option.name} [${option.shop}]`,
              value: option
            };
          })
          .sort((a, b) => {
            return (
              Number(a.value.reference.replaceAll('.', '')) -
              Number(b.value.reference.replaceAll('.', ''))
            );
          })
      });
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }

  async getProductGroups() {
    const response = await this.productGroupService.getList();

    this.groups = response.map((group) => ({
      name: `Grupo: ${group.id} - ${group.name}`,
      value: +group.id
    }));
  }

  async findFiltersOptions(): Promise<void> {
    const filterOptions = await this.filterService.getFilterList();
    filterOptions.sort((f1, f2) => {
      if (
        FormUtil.semAcento(f1.description).toUpperCase() <
        FormUtil.semAcento(f2.description).toUpperCase()
      )
        return -1;
      if (
        FormUtil.semAcento(f1.description).toUpperCase() >
        FormUtil.semAcento(f2.description).toUpperCase()
      )
        return 1;
      return 0;
    });
    this.filterOptions = filterOptions;
  }

  async getBrandsOptions() {
    this.brands = await this.categoryService
      .getCategoriesList({
        fields: ['id', 'name'],
        filters: [
          { field: 'brandCategory', condition: '', value: true },
          { field: 'isWishlist', condition: '', value: false }
        ],
        sortBy: 'name',
        sortDirection: 'asc'
      })
      .then((res) => res.content);
  }

  async findCategoryTree(categoryId?: string): Promise<void> {
    try {
      let tree = 'Sem categoria';
      if (categoryId) {
        const categories =
          await this.categoryService.getCategoriesTree(categoryId);
        tree = categories
          .reverse()
          .map((c) => `${c.reference} ${c.name}`)
          .toString()
          .replaceAll(',', ' > ');
      }
      this.form.controls['categoryTree'].setValue(tree);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async submit(): Promise<void> {
    if (this.form.valid) {
      try {
        LoaderService.showLoader();
        const product = FormUtil.getTouchedAndDirtyOnly<Partial<Product>>(
          this.form
        );

        if (Object.keys(product).length) {
          await this.productService.updateProduct({
            ...product,
            id: this.product.id,
            filters: this.form.value.filters?.map((f) => f.id) || [],
            daysOfUse: product.daysOfUse
              ? parseInt(product.daysOfUse as any)
              : BaseConnectService.NULL
          });

          if (this.product.hasVariants) {
            for (const variant of this.product.variants) {
              await this.productService.updateVariant(
                {
                  brand: product.brand,
                  category: product.category
                },
                variant.id
              );
            }
          }

          this.productUpdated.emit();
        }
        this.messageService.add({
          severity: 'success',
          detail: 'Produto atualizado com sucesso.',
          summary: 'Sucesso'
        });
      } catch (error) {
        AppDialogService.showErrorDialog(error);
      } finally {
        LoaderService.showLoader(false);
      }
    } else {
      Object.keys(this.form.controls).forEach((k) => {
        this.form.controls[k].markAsDirty();
        this.form.controls[k].markAsTouched();
        this.form.controls[k].updateValueAndValidity();
      });
    }
  }

  recalculatePrices(): void {
    this.form.controls['price'].controls['subscriberPrice'].setValue(
      (1 - this.form.value.price.subscriberDiscountPercentage / 100) *
        (this.form.value.price.price || 0)
    );
    this.form.controls['price'].controls[
      'fullPriceDiscountPercentage'
    ].setValue(
      (100 *
        ((this.form.value.price.fullPrice || 0) -
          (this.form.getRawValue().price.subscriberPrice || 0))) /
        (this.form.value.price.fullPrice || 1)
    );
  }

  addCollectionTag(event?: ChipsAddEvent) {
    if (event?.value) {
      const index = this.form.value.tagsCollection.findIndex(
        (t) => t === slugify((event.value as string).toLowerCase())
      );
      if (index >= 0 && index < this.form.value.tagsCollection.length - 1) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: `Tag já vinculada '${this.form.value.tagsCollection[index]}'`
        });
        this.form.controls['tagsCollection'].setValue(
          this.form.value.tagsCollection.slice(
            0,
            this.form.value.tagsCollection.length - 1
          )
        );
      }
    }
    this.form.controls['tagsCollection'].setValue(
      this.form.value.tagsCollection.map((t) => slugify(t.toLowerCase()))
    );
  }

  addProfileTag(event?: ChipsAddEvent) {
    if (event?.value) {
      const index = this.form.value.tagsProfile.findIndex(
        (t) => t === slugify((event.value as string).toLowerCase())
      );
      if (index >= 0 && index < this.form.value.tagsProfile.length - 1) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atenção',
          detail: `Tag já vinculada '${this.form.value.tagsProfile[index]}'`
        });
        this.form.controls['tagsProfile'].setValue(
          this.form.value.tagsProfile.slice(
            0,
            this.form.value.tagsProfile.length - 1
          )
        );
      }
    }
    this.form.controls['tagsProfile'].setValue(
      this.form.value.tagsProfile.map((t) => slugify(t.toLowerCase()))
    );
  }

  searchFilters(event: AutoCompleteCompleteEvent): void {
    this.filterOptionsAvailable =
      this.filterTags?.filter(
        (f) =>
          !this.form.value.filters?.some((cf) => cf.id === f.id) &&
          f.id.includes(slugify(FormUtil.semAcento(event.query.toLowerCase())))
      ) || [];
  }

  removeFilter(event: AutoCompleteUnselectEvent): void {
    if (event.value) {
      this.form.controls['filters'].setValue(
        this.form.value.filters.filter((f) => f !== event.value)
      );
    }
  }

  get filterTags(): FilterOption[] {
    return this.filterOptions?.reduce(
      (options, f) => (options = options.concat(f.options)),
      []
    );
  }

  createFormGroup() {
    return new FormGroup({
      name: new FormControl<string>(
        { value: this.product?.name, disabled: !this.permission },
        Validators.required
      ),
      slug: new FormControl<string>(
        { value: this.product?.slug, disabled: !this.permission },
        Validators.required
      ),
      price: new FormGroup({
        price: new FormControl<number>(
          { value: this.product?.price?.price, disabled: !this.permission },
          [Validators.required, Validators.min(0.01)]
        ),
        fullPrice: new FormControl<number>(
          { value: this.product?.price?.fullPrice, disabled: !this.permission },
          [Validators.required, Validators.min(0.01)]
        ),
        subscriberDiscountPercentage: new FormControl<number>({
          value: this.product?.price?.subscriberDiscountPercentage,
          disabled: !this.permission
        }),
        fullPriceDiscountPercentage: new FormControl<number>({
          value: this.product?.price?.fullPriceDiscountPercentage,
          disabled: true
        }),
        subscriberPrice: new FormControl<number>({
          value: this.product?.price?.subscriberPrice,
          disabled: true
        }),
        costPrice: new FormControl<number>({
          value: this.product?.costPrice,
          disabled: true
        })
      }),
      hasVariants: new FormControl<boolean>({
        value: this.product?.hasVariants,
        disabled: !this.permission
      }),
      NCM: new FormControl<string>({
        value: this.product?.NCM,
        disabled: !this.permission
      }),
      EAN: new FormControl<string>({
        value: this.product?.EAN,
        disabled: true
      }),
      CEST: new FormControl<string>({
        value: this.product?.CEST,
        disabled: !this.permission
      }),
      weight: new FormControl<number>(
        {
          value: this.product?.weight,
          disabled: !this.permission
        },
        [Validators.required, Validators.min(0.01)]
      ),
      costPrice: new FormControl<number>({
        value: this.product?.costPrice,
        disabled: true
      }),
      images: new FormControl<string[]>({
        value: this.product?.images,
        disabled: !this.permission
      }),
      published: new FormControl<boolean>({
        value: this.product?.published,
        disabled: !this.permission
      }),
      brand: new FormControl<string>(
        {
          value: this.product?.brand, //filters
          disabled: !this.permission
        },
        [Validators.required, Validators.minLength(1)]
      ),
      type: new FormControl<string>({
        value: this.product?.type,
        disabled: !this.permission
      }),
      category: new FormControl<Category>({
        value:
          this.product && this.product.category?.id
            ? this.categories?.find(
                (c) => c.id === this.product.category.id.toString()
              )
            : BaseConnectService.NULL,
        disabled: !this.permission
      }),
      categoryTree: new FormControl<string>({
        value: 'Sem categoria',
        disabled: true
      }),
      video: new FormControl<string>(
        {
          value: this.product?.video,
          disabled: !this.permission
        },
        Validators.pattern(
          /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/
        )
      ),
      gender: new FormControl<ProductGender>({
        value: this.product?.gender,
        disabled: !this.permission
      }),
      metadata: new FormGroup({
        title: new FormControl<string | undefined>(
          {
            value: this.product?.metadata?.title,
            disabled: !this.permission
          },
          [Validators.required, Validators.minLength(1)]
        ),
        description: new FormControl<string>(
          {
            value: this.product?.metadata?.description,
            disabled: !this.permission
          },
          [Validators.required, Validators.minLength(1)]
        )
      }),
      tagsCollection: new FormControl<string[]>({
        value: this.product?.tagsCollection,
        disabled: !this.permission || this.product?.hasVariants
      }),
      tagsProfile: new FormControl<string[]>({
        value: this.product?.tagsProfile,
        disabled: !this.permission || this.product?.hasVariants
      }),
      filters: new FormControl<FilterOption[]>({
        value:
          this.product && this.product.filters
            ? this.product.filters?.map((f) =>
                this.filterTags?.find((ft) => ft.id === f)
              )
            : [],
        disabled: !this.permission
      }),
      label: new FormControl<ProductLabel>({
        value: this.product?.label,
        disabled: !this.permission
      }),
      groupId: new FormControl<number>({
        value: +this.product?.groupId,
        disabled: !this.permission
      }),
      validity: new FormControl<boolean>({
        value: this.product?.validity,
        disabled: !this.permission
      }),
      daysOfUse: new FormControl<string>({
        value: this.product?.daysOfUse?.toString(),
        disabled: !this.permission
      }),
      showVariants: new FormControl<boolean>({
        value: this.product?.showVariants,
        disabled: !this.permission || !this.product?.hasVariants
      })
    });
  }

  slugify(): void {
    this.form.controls['slug'].setValue(slugify(this.form.value.name));
  }
}
